import { Grid, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CountiesDropdown } from "./CountiesDropdown";
import { EirCode } from "./EirCode";
import { IUserAddress } from "@common/Eircode/interface/IAddressForm";
import { UseFormMethods, useWatch } from "react-hook-form";
import { FieldErrors } from "react-hook-form/dist/types/errors";

interface IParams {
    addressForm: UseFormMethods<IUserAddress> & Partial<UseFormMethods>;
    buttonColor: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
    eirCodeLogoColor: "blue" | "pink";
    isEditing: boolean;
    isUpdated: boolean;
    prefix?: string;
}

export const Address = (props: IParams) => {
    const { isUpdated, addressForm, isEditing, eirCodeLogoColor, buttonColor, prefix } = props;
    const { setValue, errors, register, control } = addressForm;

    const formErrors = (prefix ? errors[prefix] : errors) as FieldErrors<IUserAddress>;
    const namePrefix = prefix ? `${prefix}.` : "";
    const idPrefix = prefix ? `${prefix}-` : "";

    const { t } = useTranslation(["address", "validation"]);

    const isFieldDisabled = !isEditing || isUpdated;

    const clearEircode = () => {
        setValue(`${namePrefix}code`, "");
    };

    const addressLine1 = useWatch<string>({ name: `${namePrefix}addressLine1`, control: addressForm.control });
    const addressLine2 = useWatch<string>({ name: `${namePrefix}addressLine2`, control: addressForm.control });
    const addressLine3 = useWatch<string>({ name: `${namePrefix}addressLine3`, control: addressForm.control });
    const town = useWatch<string>({ name: `${namePrefix}town`, control: addressForm.control });

    return (
        <Grid container justifyContent="space-between" spacing={2}>
            <EirCode
                addressForm={addressForm}
                buttonColor={buttonColor}
                eirCodeLogoColor={eirCodeLogoColor}
                isEditing={isEditing}
                isUpdated={isUpdated}
                prefix={prefix}
            />
            <Grid item xs={12}>
                <TextField
                    InputLabelProps={{ shrink: Boolean(addressLine1) }}
                    disabled={isFieldDisabled}
                    error={Boolean(formErrors?.addressLine1)}
                    fullWidth
                    helperText={formErrors?.addressLine1?.message}
                    id={`${idPrefix}addressLine1`}
                    inputRef={register({
                        required: t("validation:required"),
                        maxLength: { value: 50, message: t("validation:tooManyCharacters", { count: 50 }) },
                        minLength: { value: 2, message: t("validation:notEnoughCharacters", { count: 2 }) },
                    })}
                    label={`${t("address:addressLine1")} *`}
                    margin="dense"
                    name={`${namePrefix}addressLine1`}
                    type="text"
                    variant="standard"
                    onChange={(event) => {
                        clearEircode();

                        return event;
                    }}
                />
            </Grid>
            <Grid item sm={5} xs={12}>
                <TextField
                    InputLabelProps={{ shrink: Boolean(addressLine2) }}
                    disabled={isFieldDisabled}
                    error={Boolean(formErrors?.addressLine2)}
                    fullWidth
                    helperText={formErrors?.addressLine2?.message}
                    id={`${idPrefix}addressLine2`}
                    inputRef={register({
                        maxLength: { value: 50, message: t("validation:tooManyCharacters", { count: 50 }) },
                    })}
                    label={t("address:addressLine2")}
                    margin="dense"
                    name={`${namePrefix}addressLine2`}
                    type="text"
                    variant="standard"
                    onChange={(event) => {
                        clearEircode();

                        return event;
                    }}
                />
            </Grid>
            <Grid item sm={5} xs={12}>
                <TextField
                    InputLabelProps={{ shrink: Boolean(addressLine3) }}
                    disabled={isFieldDisabled}
                    error={Boolean(formErrors?.addressLine3)}
                    fullWidth
                    helperText={formErrors?.addressLine3?.message}
                    id={`${idPrefix}addressLine3`}
                    inputRef={register({
                        maxLength: { value: 50, message: t("validation:tooManyCharacters", { count: 50 }) },
                    })}
                    label={t("address:addressLine3")}
                    margin="dense"
                    name={`${namePrefix}addressLine3`}
                    type="text"
                    variant="standard"
                    onChange={(event) => {
                        clearEircode();

                        return event;
                    }}
                />
            </Grid>
            <Grid item md={5} xs={12}>
                <TextField
                    InputLabelProps={{ shrink: Boolean(town) }}
                    disabled={isFieldDisabled}
                    error={Boolean(formErrors?.town)}
                    fullWidth
                    helperText={formErrors?.town?.message}
                    id={`${idPrefix}town`}
                    inputRef={register({
                        required: t("validation:required"),
                    })}
                    label={`${t("address:town")} *`}
                    margin="dense"
                    name={`${namePrefix}town`}
                    type="text"
                    variant="standard"
                    onChange={(event) => {
                        clearEircode();

                        return event;
                    }}
                />
            </Grid>
            <Grid item md={5} xs={12}>
                <CountiesDropdown
                    clearEircode={clearEircode}
                    control={control}
                    error={formErrors?.county}
                    isDisabled={isFieldDisabled}
                    prefix={prefix}
                />
            </Grid>
        </Grid>
    );
};
